/**
 * The browser half of the customer email-OTP exchange.
 *
 * Authority: bead scope-r3x5; `apps/api/src/functions/auth.otp-send.ts`,
 * `auth.otp-verify.ts` (scope-9xn) — this is the fetch client for those two
 * Functions; `@nimer/contracts/auth`, `@nimer/contracts/error` for the wire
 * shapes both sides already agree on.
 *
 * A thin, pure wrapper: no React, no state, no retry. `CustomerAuthProvider`
 * (and its form component) is the only caller and owns the state machine over
 * these two calls. Neither function sets an `Authorization` header or reads
 * one — that credential belongs to the staff plane (`auth/msal-boundary.ts`)
 * and never to this one. The nonce cookie `auth.otp-send.ts` sets, and the
 * session cookie `auth.otp-verify.ts` sets on success, are both `HttpOnly` and
 * same-origin, so the browser attaches and stores them without this file
 * touching either.
 */
import { withLoading } from '../loading/loadingStore.js'
import { OtpSendResponseSchema, OtpVerifyResponseSchema } from '@nimer/contracts/auth'
import type { OtpSendResponse, OtpVerifyResponse } from '@nimer/contracts/auth'
import { ErrorEnvelopeSchema } from '@nimer/contracts/error'
import type { ErrorCode } from '@nimer/contracts/error'

export const CUSTOMER_OTP_SEND_PATH = '/api/v1/auth/otp/send'
export const CUSTOMER_OTP_VERIFY_PATH = '/api/v1/auth/otp/verify'

/** A non-2xx from either endpoint, carrying the one thing a caller can act on: the error code. */
export class OtpRequestFailed extends Error {
  readonly code: ErrorCode
  readonly retryAfterSeconds: number | null

  constructor(code: ErrorCode, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = 'OtpRequestFailed'
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
  }
}

async function postJson(path: string, body: unknown, fetchImpl: typeof fetch): Promise<unknown> {
  const response = await fetchImpl(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const parsed: unknown = await response.json().catch(() => null)
  if (response.ok) return parsed

  throw failureFrom(parsed)
}

function failureFrom(body: unknown): OtpRequestFailed {
  const envelope = ErrorEnvelopeSchema.safeParse(body)
  if (!envelope.success) return new OtpRequestFailed('INTERNAL_ERROR', 'request failed')

  const { code, message, detail } = envelope.data.error
  const retryAfter = typeof detail?.retry_after === 'number' ? detail.retry_after : null

  return new OtpRequestFailed(code, message, retryAfter)
}

export async function requestOtpCode(
  email: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<OtpSendResponse> {
  return withLoading('loading.sendingCode', async () =>
    OtpSendResponseSchema.parse(await postJson(CUSTOMER_OTP_SEND_PATH, { email }, fetchImpl)),
  )
}

export async function verifyOtpCode(
  email: string,
  code: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<OtpVerifyResponse> {
  return withLoading('loading.checkingCode', async () =>
    OtpVerifyResponseSchema.parse(
      await postJson(CUSTOMER_OTP_VERIFY_PATH, { email, code }, fetchImpl),
    ),
  )
}
